/** The Academy Pass as sold: price in FCFA, indicative price in the learner's currency, the three
 * exams, what is included, no hidden renewal, estimates only (D-110). Every value is data. */

import type { AcademyOffer } from "@agencyos/api-client";
import { asFx, asTexts, formatXaf, indicativePrice, localText } from "@agencyos/core";
import { Badge } from "@agencyos/ui-web";
import { Check, Infinity as Loop, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { dateText } from "../shared";

const EXAM_LABEL: Record<string, string> = {
  tcf_canada: "TCF Canada",
  tef_canada: "TEF Canada",
  ielts_general: "IELTS General Training",
  goethe: "Goethe-Zertifikat", // German (D-118)
};

export function PassCard({ offer, country, action, compact = false }: { offer: AcademyOffer; country: string; action?: ReactNode; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const fx = asFx(offer.fx);
  const indicative = indicativePrice(offer.price_xaf, country, fx, language === "en" ? "en-GB" : "fr-FR");
  const items = asTexts(offer.items).map((item) => localText(item, language)).filter(Boolean);
  const name = localText(offer.name as Record<string, string>, language) || t("academySales.pass.eyebrow");
  return (
    <article className="as-pass" aria-labelledby="as-pass-name">
      <div className="as-pass__top">
        <p className="as-pass__eyebrow">{t("academySales.pass.eyebrow")}</p>
        <Badge tone="success" dot>
          {t("academySales.pass.noCommitment")}
        </Badge>
      </div>
      <h2 id="as-pass-name" className="as-pass__name">
        {name}
      </h2>
      <p className="as-pass__price">
        <span className="as-pass__amount ao-num">{formatXaf(offer.price_xaf)}</span>
        <span className="as-pass__per">{t("academySales.pass.perMonth")}</span>
      </p>
      {indicative ? (
        <p className="as-pass__fx ao-num">
          {t(indicative.fixed ? "academySales.pass.indicativeFixed" : "academySales.pass.indicative", { amount: indicative.text })}
        </p>
      ) : null}
      <p className="as-pass__days">{t("academySales.pass.forDays", { days: offer.days })}</p>
      <ul className="as-pass__exams" aria-label={t("academySales.pass.exams")}>
        {offer.exams.map((exam) => (
          <li key={exam} className={`as-exam as-exam--${exam}`}>
            {EXAM_LABEL[exam] ?? exam}
          </li>
        ))}
      </ul>
      {!compact && items.length ? (
        <ul className="as-pass__items">
          {items.map((item) => (
            <li key={item}>
              <Check aria-hidden size={16} />
              <span>{item}</span>
            </li>
          ))}
          {offer.eo_credits > 0 ? (
            <li>
              <Check aria-hidden size={16} />
              <span>{t("academySales.pass.eoCredits", { count: offer.eo_credits })}</span>
            </li>
          ) : null}
        </ul>
      ) : null}
      {action}
      <p className="as-pass__note">
        <Loop aria-hidden size={14} /> {t("academySales.pass.noCommitment")}
      </p>
      <p className="as-pass__note">
        <ShieldCheck aria-hidden size={14} /> {t("academySales.pass.estimates")}
      </p>
      {indicative ? (
        <p className="as-pass__fine">
          {t("academySales.pass.indicativeNote")}
          {!indicative.fixed && fx.as_of ? ` ${t("academySales.pass.ratesDate", { date: dateText(fx.as_of, language) })}` : ""}
        </p>
      ) : null}
    </article>
  );
}
